import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import * as answer from '../../lib/answer';
import {withUser} from '../../lib/user';
import SideModal from '../../components/SideModal';
import Notification from '../../components/Notification';
import Button from '../../components/Button';
import Card from '../../components/Card';
import Answer from '../../components/Answer';
import Form from '../../components/Form';
import AnswerInput from '../../components/AnswerInput';
import SharedAnswerNotice from './SharedAnswerNotice';
import TextArea from '../../components/TextArea';

import {calculateAlerts} from './shared';
import {getRecalculatedQuestions, getRecalculations, hasAggregate} from './formulaRecalculation';
import Role from "../../lib/Role";
import {
  isQuestionDeleted,
  getQuestionDisplayLabel,
  isFormulaDefaultAnswer,
  parseDefaultAnswerFormula,
  getFormulaDefaultAnswerVariables,
  getFormulaDefaultAnswerDependents,
  FORMULA_DEFAULT_ANSWERS_ENABLED,
  isInventoryQuestion,
  getInventoryLotQuestionId,
  receivesSharedAnswers
} from "../FormBuilderScreen/shared";

class AnswersTab extends PureComponent {
  // `changedRow` is another row of the sheet when a formula on it was recalculated after this row's edit.
  // `recalculated` marks a recalculated formula answer, which keeps the row's review state.
  handleAnswersChanged = (question, answers, changedRow = this.props.row, recalculated = false) => {
    const {sheet, onAnswersChange} = this.props;
    onAnswersChange(sheet, changedRow, question, answers, recalculated);
  }

  render() {
    const {template, catalog, sheet, row, showDeleted} = this.props;
    const alerts = calculateAlerts(template, row);
    const questions = [...template.contextQuestions, ...template.questions].sort((a, b) => a.order - b.order);
    
    const answersByQuestionId = row.answers.reduce((map, answer) => {
      if (!map[answer.questionId]) {
        map[answer.questionId] = [];
      }

      map[answer.questionId].push(answer);
      return map;
    }, sheet.contextAnswers.reduce((map, answer) => {
      if (!map[answer.questionId]) {
        map[answer.questionId] = [];
      }

      map[answer.questionId].push(answer);
      return map;
    }, {}));

    return (
      <SideModal.Content>
        {alerts.length > 0 && (
          <NotificationContainer>
            <Notification warning>
              {alerts.length === 1 ? alerts[0].name : (
                <ul>
                  {alerts.map(alert => (
                    <li key={alert.id}>{alert.name}</li>
                  ))}
                </ul>
              )}
            </Notification>
          </NotificationContainer>
        )}
        <AnswerList>
            {(!showDeleted ? questions.filter(q => !isQuestionDeleted(q)) : questions).map((question, i) => (
            <AnswerEditor
              key={question.id}
              index={i + 1}
              catalog={catalog}
              questions={questions.filter(q => !isQuestionDeleted(q))}
              sheet={sheet}
              row={row}
              question={question}
              answers={answersByQuestionId[question.id] || []}
              onChange={this.handleAnswersChanged}
            />
          ))}
        </AnswerList>
      </SideModal.Content>
    );
  }
}

class AnswerEditor extends PureComponent {
  state = {
    editing: false,
    saving: false,
    validate: false,
    isValidAnswer: true,
    auditComment: '',
    recalculate: true,
    progress: null,
    values: this.props.answers.map(answer => answer.value)
  };

  toggleEditing = () => {
    this.setState(prevState => ({
      editing: !prevState.editing,
      values: this.props.answers.map(answer => answer.value),
      auditComment: '',
      recalculate: true
    }));
  }

  handleToggleRecalculate = () => {
    this.setState(prevState => ({recalculate: !prevState.recalculate}));
  }

  handleUpdateAnswers = (values, isValidAnswer) => {
    this.setState({values, isValidAnswer});
  }

  handleClearAnswers = async () => {
    this.setState({values: [], isValidAnswer: true});
  }

  handleSaveAnswers = async () => {
    const {sheet, row, question, credentials, onChange} = this.props;
    const {isValidAnswer, values, auditComment} = this.state;

    // Make sure the answer is valid.
    if (!isValidAnswer) {
      this.setState({validate: true});
      return;
    }

    // The ledger can only post an amount above zero, and the api turns anything else away.
    if (isInventoryQuestion(question) && !values.filter(value => !!value).every(value => Number(value) > 0)) {
      NotificationManager.error('Enter an amount greater than 0.');
      return;
    }

    const now = new Date().getTime();
    const answers = values
      .filter(value => !!value)
      .map((value, index) => answer.create(question.id, value, new Date(now + index)));

    // Saving the answer it already had changes nothing, so it is not sent. The API would still send a reviewed row
    // back for review, with no change in its history to show why.
    if (hasSameValues(this.props.answers, answers)) {
      this.setState({editing: false, validate: false, auditComment: ''});
      return;
    }

    try {
      this.setState({saving: true});
      await api.editAnswers(sheet.id, question.context ? null : row.id, question.id, answers, auditComment, credentials);
      onChange(question, isInventoryQuestion(question) ? withPreviousInventory(answers, this.props.answers) : answers);
      if (!question.context) {
        this.showNewLot(answers.length > 0 ? answers[0].value : null);
      }
    }
    catch (e) {
      NotificationManager.error('Your answers could not be saved! Please try again.');
      this.setState({saving: false});
      return;
    }

    if (this.state.recalculate) {
      await this.recalculateFormulas(answers.map(answer => answer.value));
    }

    this.setState({editing: false, validate: false, saving: false, auditComment: '', progress: null});
  }

  // Saves the formula answers that read the edited answer, one request at a time like the dashboard's other edits.
  // The edit itself is saved by now, so a failure here only leaves those answers to be fixed by hand.
  async recalculateFormulas(values) {
    const {sheet, row, question, questions, credentials, onChange} = this.props;
    const recalculated = getRecalculatedFormulaQuestions(question, questions);

    if (recalculated.length === 0) {
      return;
    }

    let changes;

    try {
      // A total reads every row of the sheet, not only the ones the View Data filters left loaded.
      const aggregates = recalculated.some(q => hasAggregate(parseDefaultAnswerFormula(q.defaultAnswers[0])));

      if (aggregates) {
        this.setState({progress: 'Loading the rows of the sheet…'});
      }

      const rows = aggregates ? await api.getAllSheetRows(sheet, null, credentials, undefined, 'dates.lastModifiedDate,asc') : [row];

      changes = getRecalculations({questions, rows, contextAnswers: sheet.contextAnswers, editedRow: row, question, values});
    }
    catch (e) {
      NotificationManager.error(`${formatQuestionList(recalculated)} could not be recalculated. Please edit them by hand.`);
      return;
    }

    const label = getQuestionDisplayLabel(question);
    const failed = [];
    // Changes come grouped by row, oldest first, so the count moves on whenever the row does.
    const rowIds = changes.map(change => change.row.id).filter((id, index, ids) => ids.indexOf(id) === index);

    for (const change of changes) {
      const isEditedRow = change.row.id === row.id;
      const rowNumber = rowIds.indexOf(change.row.id) + 1;

      this.setState({progress: `Recalculating row ${rowNumber} of ${rowIds.length}…`});

      const comment = `Recalculated after ${label} was edited${isEditedRow ? '' : ' on an earlier row'}.`;
      const answers = change.values.map(value => answer.create(change.question.id, value, new Date()));

      try {
        await api.editAnswers(sheet.id, change.row.id, change.question.id, answers, comment, credentials, true);
        onChange(change.question, answers, isEditedRow ? row : change.row, true);
      }
      catch (e) {
        failed.push(change);
      }
    }

    if (failed.length > 0) {
      NotificationManager.error(`${describeChanges(failed)} could not be recalculated. Please edit them by hand.`);
    }
    else if (changes.length > 0) {
      NotificationManager.success(`Recalculated ${describeChanges(changes)}.`);
    }
  }

  // The api moves the row's inventory answers that read their lot from this question to the new lot, so they show it
  // straight away rather than after a reload.
  showNewLot = lot => {
    const {row, question, questions, onChange} = this.props;

    questions
      .filter(q => getInventoryLotQuestionId(q) === question.id)
      .forEach(inventoryQuestion => {
        const answers = row.answers.filter(a => a.questionId === inventoryQuestion.id);
        if (answers.length > 0) {
          onChange(inventoryQuestion, answers.map(a => a.inventory ? {...a, inventory: {...a.inventory, lot}} : a));
        }
      });
  }

  handleChangeAuditComment = e => {
    this.setState({auditComment: e.target.value});
  }

  render() {
    const {user, catalog, index, question, questions, answers} = this.props;
    const {editing, saving, validate, isValidAnswer, values, auditComment, recalculate, progress} = this.state;
    const formulaVariables = hasFormulaDefaultAnswer(question) ? getFormulaDefaultAnswerVariables(question, questions) : [];
    const recalculated = getRecalculatedFormulaQuestions(question, questions);
    // Sheet setup edits are not recalculated, so those still get the note asking for both answers to be edited.
    const formulaDependents = FORMULA_DEFAULT_ANSWERS_ENABLED && recalculated.length === 0
      ? getFormulaDefaultAnswerDependents(question, questions)
      : [];
    const aggregates = recalculated.some(q => hasAggregate(parseDefaultAnswerFormula(q.defaultAnswers[0])));

    return (
      <AnswerListItem key={question.id}>
        <Form onSubmit={this.handleSaveAnswers}>
          <AnswerListItemHeader>
            <h4><span>{index}.</span> {getQuestionDisplayLabel(question)}</h4>
            {canEditRow(question, user) && (
              editing ? (
                <>
                  {values && values.length > 0 && !isInventoryQuestion(question) && (<Button tertiary disabled={saving} onClick={this.handleClearAnswers}>Clear</Button>)}
                  <Button tertiary onClick={this.toggleEditing}>Cancel</Button>
                  <Button type="submit" disabled={saving || (validate && !isValidAnswer)}>Save</Button>
                </>
              ) : (
                <Button tertiary onClick={this.toggleEditing}>Edit</Button>
              )
            )}
          </AnswerListItemHeader>
          <AnswerListItemContent>
            {editing ? (
              <>
                {hasFormulaDefaultAnswer(question) && (
                  <Form.Group>
                    <Notification info mini>
                      This answer is worked out by a formula{formulaVariables.length > 0 ? ` using ${formatQuestionList(formulaVariables)}` : ''}.
                      {question.context ? (
                        <>
                          {' '}Editing the answers the formula reads does not recalculate it for this sheet row, so edit
                          both answers if you want them to match on this submission.
                        </>
                      ) : (
                        <>
                          {' '}An answer typed here is replaced if an answer the formula reads is edited and recalculated
                          later.
                        </>
                      )}
                    </Notification>
                  </Form.Group>
                )}
                {recalculated.length > 0 && (
                  <Form.Group>
                    <CheckboxOption>
                      <input type="checkbox" checked={recalculate} disabled={saving} onChange={this.handleToggleRecalculate}/>
                      <span>Recalculate answers from question: "{formatQuestionList(recalculated)}" to match</span>
                    </CheckboxOption>
                    <HelperText>
                      {aggregates
                        ? 'Totals add up every row, so this row and every row submitted after it are updated. '
                        : 'Only this row is updated. '}
                      Each updated answer is saved as an edit with its own entry in the row's history. Other rows
                      keep their review status.
                      {!recalculate && ' Left unticked, edit both answers if you want them to match.'}
                    </HelperText>
                    {progress && (
                      <ProgressText role="status">{progress}</ProgressText>
                    )}
                  </Form.Group>
                )}
                {formulaDependents.length > 0 && (
                  <Form.Group>
                    <Notification info mini>
                      {formatQuestionList(formulaDependents)} {formulaDependents.length === 1 ? 'is' : 'are'} worked
                      out by a formula that uses this answer. Editing this answer does not recalculate
                      {' '}{formulaDependents.length === 1 ? 'it' : 'them'} for this sheet row, so edit both answers if
                      you want them to match on this submission.
                    </Notification>
                  </Form.Group>
                )}
                {isInventoryQuestion(question) && (
                  <Form.Group>
                    <Notification info mini>
                      Saving reverses this answer's stock movement and posts the new amount, or holds it until the submission
                      is approved if the form waits for approval. The item and lot stay the same.
                    </Notification>
                  </Form.Group>
                )}
                {isInventoryLotQuestion(question, questions) && (
                  <Form.Group>
                    <Notification info mini>
                      Inventory answers read their lot from this answer. Saving moves their stock to the new lot.
                    </Notification>
                  </Form.Group>
                )}
                {receivesSharedAnswers(question) && (
                  <SharedAnswerNotice question={question} currentValue={answers.length > 0 ? answers[0].value : null} />
                )}
                <Form.Group>
                  <AnswerInput
                    label="Answer"
                    invalid={validate}
                    question={question}
                    catalog={catalog}
                    values={values}
                    onChange={this.handleUpdateAnswers}
                    autoOpen
                  />
                </Form.Group>
                <Form.Group>
                  <TextArea
                    id={`audit-comment-${question.id}`}
                    label="Reason for Change"
                    placeholder="Add a comment explaining why you're editing this answer"
                    value={auditComment}
                    onChange={this.handleChangeAuditComment}
                  />
                </Form.Group>
              </>
            ) : (
              <Answer question={question} answers={answers} catalog={catalog} />
            )}
          </AnswerListItemContent>
        </Form>
      </AnswerListItem>
    );
  }
}

AnswerEditor = withUser(AnswerEditor);

// Formula default answers are held back by FORMULA_DEFAULT_ANSWERS_ENABLED, so until that is turned on a
// stored formula is treated as a plain default answer and needs no note.
function hasFormulaDefaultAnswer(question) {
  return FORMULA_DEFAULT_ANSWERS_ENABLED && isFormulaDefaultAnswer(question.defaultAnswers);
}

function hasSameValues(current, next) {
  return current.length === next.length && current.every((answer, index) => String(answer.value) === String(next[index].value));
}

function getRecalculatedFormulaQuestions(question, questions) {
  return FORMULA_DEFAULT_ANSWERS_ENABLED ? getRecalculatedQuestions(question, questions) : [];
}

// e.g. "Total on 3 rows", for the notifications after a recalculation.
function describeChanges(changes) {
  const questions = changes
    .map(change => change.question)
    .filter((question, index, list) => list.findIndex(q => q.id === question.id) === index);
  const rowCount = new Set(changes.map(change => change.row.id)).size;

  return `${formatQuestionList(questions)} on ${rowCount} ${rowCount === 1 ? 'row' : 'rows'}`;
}

function formatQuestionList(questions) {
  const labels = questions.map(getQuestionDisplayLabel);

  if (labels.length < 2) {
    return labels.join('');
  }

  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function canEditRow(question, user) {
  return user.role === Role.ADMIN && question.type !== 'SIGNATURE';
}

// The api keeps the item and lot of the answers an inventory edit replaces, in order, so the edited answers shown here
// keep them too. They are only added to the copy shown, never sent.
function withPreviousInventory(answers, previous) {
  if (previous.length === 0) {
    return answers;
  }
  return answers.map((edited, i) => ({...edited, inventory: previous[Math.min(i, previous.length - 1)].inventory}));
}

// Whether some inventory question on the form reads its lot from this question's answer.
function isInventoryLotQuestion(question, questions) {
  return questions.some(q => getInventoryLotQuestionId(q) === question.id);
}

const NotificationContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  input {
    width: 1rem;
    height: 1rem;
    margin: 0;
    cursor: pointer;
    accent-color: #0049ee;
    flex-shrink: 0;
  }
`;

const HelperText = styled.p`
  margin: 0.25rem 0 0 1.5rem;
  color: #747474;
  font-size: 0.875rem;
  line-height: 1.4;
`;

const ProgressText = styled.p`
  margin: 0.5rem 0 0 1.5rem;
  color: #0049ee;
  font-size: 0.875rem;
  font-weight: 500;
`;

const AnswerList = styled.div`
  display: flex;
  flex-direction: column;
`;

const AnswerListItem = styled(Card)`
  margin-bottom: 1.5rem;

  :last-child {
    margin-bottom: 0;
  }
`;

const AnswerListItemHeader = styled.div`
  display: flex;
  padding: 1rem 1rem 0.5rem 2rem;

  > * {
    margin-left: 0.5rem;
  }

  h4 {
    margin: 0;
    flex: 1;
    display: flex;
    align-items: center;
    font-size: 1.125rem;
    line-height: 1.3;
    font-weight: 500;

    span {
      min-width: 2rem;
    }
  }
`;

const AnswerListItemContent = styled.div`
  line-height: 1.5;
  padding: 0.5rem 1rem 1.5rem 4rem;
`;

export default AnswersTab;